<template>
  <div>
    <gg-loading v-if="loading && !aliases.length" :loading="true" type="card" />

    <!-- Only the empty state while the mailbox has no aliases (AM-1107). -->
    <feature-setup v-else-if="!aliases.length" feature="aliases" @create="adding = true" />

    <gg-card v-else>
      <gg-card-header>
        <div class="d-flex align-center flex-wrap">
          <span class="mr-auto">Aliases</span>
          <gg-btn secondary small class="my-1" @click="adding = true">
            <v-icon left small>mdi-plus</v-icon>Create Alias
          </gg-btn>
        </div>
      </gg-card-header>
      <gg-card-text>
        <p class="body-2 mb-4">
          Mail sent to an alias is delivered to {{ mailbox.email }}.
        </p>

        <table-wrapper>
          <v-data-table
            :headers="headers"
            :items="aliases"
            :loading="loading"
            loading-text="Loading aliases…"
            item-key="dest"
            sort-by="dest"
          >
            <template v-slot:item.actions="{ item }">
              <div class="text-right">
                <gg-btn
                  small
                  secondary
                  :aria-label="`Delete alias ${item.dest}`"
                  @click="confirmDelete(item)"
                >
                  Delete
                </gg-btn>
              </div>
            </template>
          </v-data-table>
        </table-wrapper>
      </gg-card-text>
    </gg-card>

    <delete-alias-modal
      :is-active="confirming"
      :alias="selected"
      :mailbox="mailbox"
      @close="confirming = false"
      @deleted="onChanged"
    />

    <add-alias-modal
      :is-active="adding"
      :mailbox="mailbox"
      @close="adding = false"
      @created="onChanged"
    />
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import AddAliasModal from '@/components/Email/AddAliasModal'
import DeleteAliasModal from '@/components/Email/DeleteAliasModal'
import TableWrapper from '@/components/Common/TableWrapper'
import FeatureSetup from '@/components/Email/FeatureSetup'

export default {
  name: 'MailboxAliasesView',
  components: {
    AddAliasModal,
    DeleteAliasModal,
    TableWrapper,
    FeatureSetup,
  },
  props: {
    mailbox: {
      type: Object,
      required: true,
    },
  },
  data () {
    return {
      // `dest` is the alias's own address; `forward` is this mailbox. The column
      // showing where mail goes is left out because it is the same on every row.
      headers: [
        { text: 'Alias', value: 'dest' },
        { text: 'Actions', value: 'actions', align: 'end', sortable: false },
      ],
      aliases: [],
      loading: true,
      adding: false,
      confirming: false,
      selected: null,
    }
  },
  methods: {
    ...mapActions('emailHub', ['loadAliases']),
    ...mapActions(['reportError']),
    async load () {
      this.loading = true
      try {
        this.aliases = await this.loadAliases({
          serviceId: this.mailbox.serviceId,
          email: this.mailbox.email,
        })
      } catch (e) {
        this.aliases = []
        this.reportError('Unable to load aliases for this mailbox.')
      } finally {
        this.loading = false
      }
    },
    confirmDelete (alias) {
      this.selected = alias
      this.confirming = true
    },
    /**
     * An alias is a forwarder on this service, so re-reading the list is not
     * enough on its own: `changed` sends the account tab back through
     * `loadServiceMailboxes`, which is what keeps the per-mailbox alias count
     * on the Accounts tab honest.
     */
    async onChanged () {
      await this.load()
      this.$emit('changed')
    },
  },
  created () {
    this.load()
  },
}
</script>
